import axios from "axios";
import { API_BASE_URL } from "../../../consts";

export const ACCEPTED_VIDEO_TYPES = [
  "video/mp4",
  "video/quicktime",
  "video/webm",
];
const MIME_BY_EXTENSION = {
  mp4: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
};
export const detectVideoType = (source, type) => {
  const ext = (source?.name || "").split(".").pop().toLowerCase();
  return Promise.resolve(MIME_BY_EXTENSION[ext] || type);
};
export const MAX_FILE_SIZE_MB = 2048;
export const MIN_TITLE_LENGTH = 3;
export const MAX_TITLE_LENGTH = 100;

const EMPTY_METADATA = { duration: null, width: null, height: null };
const METADATA_TIMEOUT_MS = 8000;
const THUMBNAIL_MAX_WIDTH = 480;

const waitForEvent = (target, event) =>
  new Promise((resolve, reject) => {
    target.addEventListener(event, resolve, { once: true });
    target.addEventListener("error", reject, { once: true });
  });

const isFrameBlack = (canvas) => {
  const { data } = canvas
    .getContext("2d")
    .getImageData(0, 0, canvas.width, canvas.height);
  let sum = 0;
  let samples = 0;
  for (let i = 0; i < data.length; i += 40) {
    sum += data[i] + data[i + 1] + data[i + 2];
    samples += 3;
  }
  return sum / samples < 8;
};

const captureFrame = (videoEl) => {
  const scale = Math.min(1, THUMBNAIL_MAX_WIDTH / videoEl.videoWidth);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(videoEl.videoWidth * scale);
  canvas.height = Math.round(videoEl.videoHeight * scale);
  canvas.getContext("2d").drawImage(videoEl, 0, 0, canvas.width, canvas.height);
  return canvas;
};

const canvasToBlob = (canvas) =>
  new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.8));

const captureThumbnail = async (videoEl) => {
  const duration = videoEl.duration || 0;
  const seekTimes = [Math.min(2, duration / 2)];
  if (duration > 4) seekTimes.push(duration * 0.5);

  let canvas = null;
  for (const time of seekTimes) {
    if (!(time > 0)) continue;
    videoEl.currentTime = time;
    await waitForEvent(videoEl, "seeked");
    canvas = captureFrame(videoEl);
    if (!isFrameBlack(canvas)) break;
  }

  return canvas ? canvasToBlob(canvas) : null;
};

const extractVideoMetadataAndThumbnail = async (file) => {
  const videoEl = document.createElement("video");
  videoEl.preload = "auto";
  videoEl.muted = true;
  const url = URL.createObjectURL(file);
  videoEl.src = url;

  const run = async () => {
    await waitForEvent(videoEl, "loadedmetadata");
    const metadata = {
      duration: Math.round(videoEl.duration) || null,
      width: videoEl.videoWidth || null,
      height: videoEl.videoHeight || null,
    };
    const thumbnailBlob = await captureThumbnail(videoEl).catch(() => null);
    return { ...metadata, thumbnailBlob };
  };

  try {
    return await Promise.race([
      run(),
      new Promise((resolve) =>
        setTimeout(
          () => resolve({ ...EMPTY_METADATA, thumbnailBlob: null }),
          METADATA_TIMEOUT_MS
        )
      ),
    ]);
  } catch {
    return { ...EMPTY_METADATA, thumbnailBlob: null };
  } finally {
    URL.revokeObjectURL(url);
  }
};

const computeChecksum = async (file) => {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

export function useVideoUploadPipeline({
  getToken,
  getShowId,
  getTitle,
  toast,
  onServerUploadChange,
}) {
  const reject = (error, severity, summary, detail) => {
    toast.add({ severity, summary, detail, life: 6000 });
    error(summary);
  };

  const serverConfig = {
    process: (fieldName, file, metadata, load, error, progress, abort) => {
      const controller = new AbortController();

      (async () => {
        try {
          const { thumbnailBlob, ...videoMeta } =
            await extractVideoMetadataAndThumbnail(file);

          const checksum = await computeChecksum(file);
          const title = getTitle().trim();
          const showId = getShowId();
          const authHeaders = { Authorization: `Bearer ${getToken()}` };

          const dupResponse = await axios.get(
            `${API_BASE_URL}/podcast-videos/check-duplicate`,
            {
              headers: authHeaders,
              params: { checksum, showId, title },
              signal: controller.signal,
            }
          );
          const duplicate = dupResponse.data?.data;

          if (duplicate?.exists && duplicate.reason === "checksum") {
            const existing = duplicate.video;
            reject(
              error,
              "warn",
              "Already uploaded",
              `This exact video was already uploaded to "${
                existing.show_title
              }" on ${new Date(existing.created_at).toLocaleDateString()}.`
            );
            return;
          }

          if (duplicate?.exists && duplicate.reason === "title") {
            reject(
              error,
              "warn",
              "Title already used",
              `"${title}" already exists for this show. Choose a different title.`
            );
            return;
          }

          const formData = new FormData();
          formData.append("video", file, file.name);
          formData.append("showId", showId);
          formData.append("title", title);
          formData.append("checksum", checksum);
          if (videoMeta.duration)
            formData.append("duration", videoMeta.duration);
          if (videoMeta.width) formData.append("width", videoMeta.width);
          if (videoMeta.height) formData.append("height", videoMeta.height);
          if (thumbnailBlob)
            formData.append("thumbnail", thumbnailBlob, "thumbnail.jpg");

          onServerUploadChange?.(true);
          let response;
          try {
            response = await axios.post(
              `${API_BASE_URL}/podcast-videos/upload`,
              formData,
              {
                headers: authHeaders,
                signal: controller.signal,
                onUploadProgress: (event) => {
                  if (!event.total) return;
                  if (event.loaded >= event.total) {
                    progress(false, 0, 0);
                  } else {
                    progress(true, event.loaded, event.total);
                  }
                },
              }
            );
          } finally {
            onServerUploadChange?.(false);
          }

          load(response.data?.data?.videoId || "uploaded");
        } catch (err) {
          if (axios.isCancel(err)) return;
          const message = err.response?.data?.message || "Upload failed";
          const isDuplicate = err.response?.status === 409;
          reject(
            error,
            isDuplicate ? "warn" : "error",
            isDuplicate ? "Already uploaded" : "Upload failed",
            message
          );
        }
      })();

      return {
        abort: () => {
          controller.abort();
          abort();
        },
      };
    },
  };

  return { serverConfig };
}
